import * as Sentry from '@sentry/browser';

const _VUMEDI_PREFIX = '_VUMEDI_';

export const UserStoreKey = {
    PlaybackRate: 'PLAYBACK_RATE',
    VolumeLevel: 'VOLUME_LEVEL',
    VAIATooltipSeen: 'VAIA_TOOLTIP_SEEN',
    AskmediNewQuestionTooltipDismissed: 'ASKMEDI_NEW_QUESTION_TOOLTIP_DISMISSED',
} as const;

type UserStoreKeyValue = (typeof UserStoreKey)[keyof typeof UserStoreKey];

export const UserStore = {
    _getStorage(): Storage | null {
        if (window.localStorage) {
            return window.localStorage;
        }

        if (window.sessionStorage) {
            return window.sessionStorage;
        }

        return null;
    },
    _load(key: UserStoreKeyValue): string | null {
        const storage = this._getStorage();

        if (!storage) {
            return null;
        }

        try {
            return storage.getItem(`${_VUMEDI_PREFIX}${key}`);
        } catch {
            Sentry.withScope((scope) => {
                const err = new Error(
                    `UserStore._load: could not retrieve key '${_VUMEDI_PREFIX}${key}' from storage '${storage}'.`,
                );

                scope.setFingerprint(['UserStore._load read error']);
                Sentry.captureException(err);
            });

            return null;
        }
    },
    _save(key: UserStoreKeyValue, value: unknown): void {
        const storage = this._getStorage();

        if (!storage) {
            return;
        }

        try {
            storage.setItem(`${_VUMEDI_PREFIX}${key}`, JSON.stringify(value));
        } catch {
            Sentry.withScope((scope) => {
                const err = new Error(
                    `UserStore._save: could not save key '${_VUMEDI_PREFIX}${key}' to storage '${storage}'.`,
                );

                scope.setFingerprint(['UserStore._save error']);
                Sentry.captureException(err);
            });
        }
    },
    loadPlaybackRate(): number | undefined {
        const rateString = this._load(UserStoreKey.PlaybackRate);
        const rateNumber = window.parseFloat(rateString ?? '');

        if (Number.isNaN(rateNumber)) {
            return undefined;
        }

        return rateNumber;
    },
    savePlaybackRate(rate: number): void {
        this._save(UserStoreKey.PlaybackRate, rate);
    },
    loadVolumeLevel(): number | undefined {
        const volumeString = this._load(UserStoreKey.VolumeLevel);
        const volumeNumber = window.parseFloat(volumeString ?? '');

        if (Number.isNaN(volumeNumber)) {
            return undefined;
        }

        return volumeNumber;
    },
    saveVolumeLevel(level: number): void {
        this._save(UserStoreKey.VolumeLevel, level);
    },
    loadVAIATooltipSeen(): boolean {
        const seenString = this._load(UserStoreKey.VAIATooltipSeen);
        const seenParsed = seenString === null ? null : JSON.parse(seenString);

        return !!seenParsed;
    },
    saveVAIATooltipSeen(seen: boolean = true): void {
        this._save(UserStoreKey.VAIATooltipSeen, !!seen);
    },
    loadAskmediNewQuestionTooltipDismissed(): boolean {
        const dismissedString = this._load(UserStoreKey.AskmediNewQuestionTooltipDismissed);
        const dismissedParsed = dismissedString === null ? null : JSON.parse(dismissedString);

        return !!dismissedParsed;
    },
    saveAskmediNewQuestionTooltipDismissed(dismissed: boolean = true): void {
        this._save(UserStoreKey.AskmediNewQuestionTooltipDismissed, !!dismissed);
    },
};
